import React, { useContext, useEffect, useRef, useState } from 'react'
import {
  Avatar,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Divider,
  FormControl,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  Tooltip,
  Typography,
  Dialog,
  DialogContent,
} from '@mui/material'
import {
  ArrowBack,
  Send,
  AttachFile,
  AccessTime,
  Person,
  Category,
  Close,
  InsertDriveFile,
  Lock,
  Verified,
  Mic,
  Stop,
  FiberManualRecord,
  Download,
  DoneAll,
  Done,
  Reply,
  PushPin,
  PersonAdd,
  SentimentVerySatisfied,
  SentimentSatisfied,
  SentimentNeutral,
  SentimentDissatisfied,
  SentimentVeryDissatisfied,
  OpenInNew,
  PictureAsPdf,
  TableChart,
  Description,
  FolderZip,
  TextSnippet,
  PlayArrow,
  Pause,
  SmartToy,
  Delete,
  RecordVoiceOver,
} from '@mui/icons-material'
import { useNavigate, useParams } from 'react-router-dom'
import { useSnackbar } from 'notistack'
import { api } from '../../../../services/Main/Api'
import Context from '../../../../contexts/Context'
import moment from 'moment'
import 'moment/locale/pt-br'
import useAudioRecorder, { isAudioFile, isAudioMimeType } from '../../../../hooks/useAudioRecorder'
import Markdown from 'react-markdown'
import remarkBreaks from 'remark-breaks'
import MarkdownEditor from '../../../../components/Ticket/MarkdownEditor'
import InvestigationSteps from '../../../../components/Ticket/InvestigationSteps'
import TicketAIInvestigator from '../../../../components/Ticket/TicketAIInvestigator'

const formatAudioTime = (seconds) => {
  if (!seconds || !isFinite(seconds)) return '0:00'
  const m = Math.floor(seconds / 60)
  const s = Math.floor(seconds % 60)
  return `${m}:${s.toString().padStart(2, '0')}`
}

const AudioPlayer = ({ src, attachmentId, ticketId, existingTranscription }) => {
  const audioRef = React.useRef(null)
  const [playing, setPlaying] = React.useState(false)
  const [currentTime, setCurrentTime] = React.useState(0)
  const [duration, setDuration] = React.useState(0)
  const durationProbed = React.useRef(false)
  const [transcribing, setTranscribing] = React.useState(false)
  const [transcription, setTranscription] = React.useState(existingTranscription || '')
  const [showTranscription, setShowTranscription] = React.useState(!!existingTranscription)

  const updateDuration = () => {
    const d = audioRef.current?.duration
    if (d && isFinite(d) && d > 0) setDuration(d)
  }

  const probeDuration = () => {
    const audio = audioRef.current
    if (!audio || durationProbed.current) return
    if (audio.duration && isFinite(audio.duration) && audio.duration > 0) {
      setDuration(audio.duration)
      return
    }
    durationProbed.current = true
    const onDurChange = () => {
      if (audio.duration && isFinite(audio.duration)) {
        setDuration(audio.duration)
        audio.currentTime = 0
        audio.removeEventListener('durationchange', onDurChange)
      }
    }
    audio.addEventListener('durationchange', onDurChange)
    audio.currentTime = 1e101
  }

  React.useEffect(() => {
    const audio = audioRef.current
    if (!audio) return
    audio.addEventListener('loadedmetadata', probeDuration)
    audio.addEventListener('canplay', probeDuration)
    if (audio.readyState >= 1) probeDuration()
    return () => {
      audio.removeEventListener('loadedmetadata', probeDuration)
      audio.removeEventListener('canplay', probeDuration)
    }
  }, [src])

  const toggle = () => {
    if (!audioRef.current) return
    if (playing) { audioRef.current.pause() } else { audioRef.current.play() }
    setPlaying(!playing)
  }

  const handleTranscribe = async () => {
    if (!attachmentId || !ticketId) return
    setTranscribing(true)
    try {
      const res = await api.post(`tickets/${ticketId}/attachments/${attachmentId}/transcribe/`)
      if (res.data.transcription) {
        setTranscription(res.data.transcription)
        setShowTranscription(true)
      }
    } catch (err) {
      console.error('Erro ao transcrever:', err)
    } finally {
      setTranscribing(false)
    }
  }

  return (
    <Box>
      <Box sx={{
        display: 'inline-flex', alignItems: 'center', gap: 1, px: 1.5, py: 0.75,
        borderRadius: 6, bgcolor: '#F1F5F9', border: '1px solid #E2E8F0', maxWidth: 400,
      }}>
        <audio
          ref={audioRef}
          src={src}
          preload="auto"
          onTimeUpdate={() => {
            setCurrentTime(audioRef.current?.currentTime || 0)
            updateDuration()
          }}
          onEnded={() => setPlaying(false)}
        />
        <IconButton size="small" onClick={toggle} sx={{ bgcolor: '#0073ea', color: '#fff', width: 30, height: 30, '&:hover': { bgcolor: '#005bb5' } }}>
          {playing ? <Pause sx={{ fontSize: 16 }} /> : <PlayArrow sx={{ fontSize: 16 }} />}
        </IconButton>
        <Box sx={{ flex: 1, minWidth: 100 }}>
          <Box
            component="input"
            type="range"
            min={0}
            max={duration || 0}
            value={currentTime}
            onChange={(e) => { if (audioRef.current) audioRef.current.currentTime = e.target.value }}
            sx={{
              width: '100%', height: 4, appearance: 'none', bgcolor: '#CBD5E1', borderRadius: 2, outline: 'none', cursor: 'pointer',
              '&::-webkit-slider-thumb': { appearance: 'none', width: 12, height: 12, borderRadius: '50%', bgcolor: '#0073ea', cursor: 'pointer' },
            }}
          />
        </Box>
        <Typography variant="caption" sx={{ color: '#64748B', fontFamily: 'monospace', fontSize: 11, minWidth: 70, textAlign: 'right' }}>
          {formatAudioTime(currentTime)} / {formatAudioTime(duration)}
        </Typography>
        {attachmentId && !transcription && (
          <Tooltip title="Transcrever áudio" arrow>
            <IconButton
              size="small"
              onClick={handleTranscribe}
              disabled={transcribing}
              sx={{ color: '#7C3AED', '&:hover': { bgcolor: '#F5F3FF' } }}
            >
              {transcribing ? <CircularProgress size={16} /> : <RecordVoiceOver sx={{ fontSize: 16 }} />}
            </IconButton>
          </Tooltip>
        )}
        {transcription && (
          <Tooltip title={showTranscription ? "Ocultar transcrição" : "Ver transcrição"} arrow>
            <IconButton
              size="small"
              onClick={() => setShowTranscription(!showTranscription)}
              sx={{ color: '#7C3AED' }}
            >
              <RecordVoiceOver sx={{ fontSize: 16 }} />
            </IconButton>
          </Tooltip>
        )}
      </Box>
      {showTranscription && transcription && (
        <Box sx={{ mt: 0.5, px: 1.5, py: 1, bgcolor: '#F5F3FF', borderRadius: 2, border: '1px solid #DDD6FE', maxWidth: 400 }}>
          <Typography variant="caption" sx={{ color: '#7C3AED', fontWeight: 600, fontSize: 10, textTransform: 'uppercase' }}>
            Transcrição
          </Typography>
          <Typography variant="body2" sx={{ mt: 0.5, fontSize: 13, color: '#475569', whiteSpace: 'pre-wrap' }}>
            {transcription}
          </Typography>
        </Box>
      )}
    </Box>
  )
}

const RATING_CONFIG = {
  5: { icon: SentimentVerySatisfied, label: 'Ótimo', color: '#16A34A' },
  4: { icon: SentimentSatisfied, label: 'Bom', color: '#65A30D' },
  3: { icon: SentimentNeutral, label: 'Regular', color: '#D97706' },
  2: { icon: SentimentDissatisfied, label: 'Ruim', color: '#EA580C' },
  1: { icon: SentimentVeryDissatisfied, label: 'Péssimo', color: '#DC2626' },
}

const STATUS_COLORS = {
  OPEN: { bg: '#DBEAFE', color: '#1E40AF', label: 'Aberto' },
  IN_PROGRESS: { bg: '#FEF3C7', color: '#92400E', label: 'Em Andamento' },
  WAITING_CLIENT: { bg: '#FEE2E2', color: '#991B1B', label: 'Aguardando Cliente' },
  RESOLVED: { bg: '#D1FAE5', color: '#065F46', label: 'Finalizado' },
}

const PRIORITY_COLORS = {
  LOW: { bg: '#F3F4F6', color: '#6B7280', label: 'Baixa' },
  MEDIUM: { bg: '#DBEAFE', color: '#1D4ED8', label: 'Média' },
  HIGH: { bg: '#FEF3C7', color: '#D97706', label: 'Alta' },
  URGENT: { bg: '#FEE2E2', color: '#DC2626', label: 'Urgente' },
}

const getFileTypeInfo = (filename) => {
  const ext = (filename || '').split('.').pop()?.toLowerCase()
  if (['pdf'].includes(ext)) return { icon: PictureAsPdf, color: '#DC2626', bg: '#FEE2E2', label: 'PDF' }
  if (['doc', 'docx'].includes(ext)) return { icon: Description, color: '#2563EB', bg: '#DBEAFE', label: 'DOC' }
  if (['xls', 'xlsx', 'csv'].includes(ext)) return { icon: TableChart, color: '#16A34A', bg: '#D1FAE5', label: 'XLS' }
  if (['zip', 'rar', '7z', 'tar', 'gz'].includes(ext)) return { icon: FolderZip, color: '#D97706', bg: '#FEF3C7', label: 'ZIP' }
  if (['txt', 'log'].includes(ext)) return { icon: TextSnippet, color: '#64748B', bg: '#F1F5F9', label: 'TXT' }
  return { icon: InsertDriveFile, color: '#94A3B8', bg: '#F1F5F9', label: ext?.toUpperCase() || 'FILE' }
}

const AdminTicketDetail = () => {
  const { id } = useParams()
  const navigate = useNavigate()
  const { enqueueSnackbar } = useSnackbar()
  const { user } = useContext(Context)
  const [ticket, setTicket] = useState(null)
  const [loading, setLoading] = useState(true)
  const draftKey = `ticket_draft_admin_${id}`
  const [newComment, setNewComment] = useState(() => localStorage.getItem(draftKey) || '')
  const [isInternal, setIsInternal] = useState(false)
  const [sendingComment, setSendingComment] = useState(false)
  const markdownEditorRef = React.useRef(null)
  const [pendingFiles, setPendingFiles] = useState([])
  const [imagePreview, setImagePreview] = useState(null)
  const [replyOpen, setReplyOpen] = useState(false)
  const [feedbackDetailOpen, setFeedbackDetailOpen] = useState(false)
  const [aiInvestigateOpen, setAiInvestigateOpen] = useState(false)

  const { isRecording, formattedTime, startRecording, stopRecording, cancelRecording } = useAudioRecorder({
    onRecordingComplete: (file) => setPendingFiles(prev => [...prev, file]),
    onError: (msg) => enqueueSnackbar(msg, { variant: 'error' }),
  })
  const [statusLoading, setStatusLoading] = useState(false)
  const fileInputRef = useRef(null)

  const fetchTicket = () => {
    api.get(`tickets/${id}/`)
      .then(res => setTicket(res.data))
      .catch(err => {
        console.error(err)
        enqueueSnackbar('Erro ao carregar ticket.', { variant: 'error' })
      })
      .finally(() => setLoading(false))
  }

  useEffect(() => { fetchTicket() }, [id])

  const handleTogglePin = () => {
    api.post(`tickets/${id}/pin/`)
      .then(res => {
        setTicket(prev => ({ ...prev, is_pinned: res.data.pinned }))
        enqueueSnackbar(res.data.pinned ? 'Ticket em acompanhamento' : 'Acompanhamento removido', { variant: 'success' })
      })
      .catch(() => enqueueSnackbar('Erro ao alterar acompanhamento.', { variant: 'error' }))
  }

  const handleStatusChange = (newStatus) => {
    setStatusLoading(true)
    api.post(`tickets/${id}/change-status/`, { status: newStatus })
      .then(res => {
        setTicket(res.data)
        enqueueSnackbar('Status atualizado!', { variant: 'success' })
      })
      .catch(err => {
        console.error(err)
        enqueueSnackbar('Erro ao atualizar status.', { variant: 'error' })
      })
      .finally(() => setStatusLoading(false))
  }

  const handleFileSelect = (e) => {
    const selectedFiles = Array.from(e.target.files || [])
    const maxSize = 10 * 1024 * 1024
    const validFiles = []
    selectedFiles.forEach(file => {
      if (file.size > maxSize) {
        enqueueSnackbar(`"${file.name}" excede 10MB.`, { variant: 'warning' })
      } else {
        validFiles.push(file)
      }
    })
    setPendingFiles(prev => [...prev, ...validFiles])
    if (fileInputRef.current) fileInputRef.current.value = ''
  }

  const handleRemovePendingFile = (index) => {
    setPendingFiles(prev => prev.filter((_, i) => i !== index))
  }

  const handleDeleteComment = (commentId) => {
    if (!window.confirm('Tem certeza que deseja excluir esta nota interna?')) return
    api.delete(`tickets/${id}/comments/${commentId}/`)
      .then(() => {
        enqueueSnackbar('Nota interna excluída', { variant: 'success' })
        fetchTicket()
      })
      .catch(() => enqueueSnackbar('Erro ao excluir nota.', { variant: 'error' }))
  }

  const handleSendComment = async (e) => {
    e.preventDefault()
    if (!newComment.trim() && pendingFiles.length === 0) return

    setSendingComment(true)
    try {
      const message = newComment.trim() || (pendingFiles.some(f => isAudioMimeType(f.type)) ? 'Mensagem de áudio' : 'Anexo enviado')
      const commentRes = await api.post(`tickets/${id}/comments/`, {
        ticket: id,
        message,
        is_internal: isInternal,
      })
      const commentId = commentRes.data?.id

      // Link inline image attachments to comment
      if (commentId && markdownEditorRef.current?.getInlineAttachmentIds) {
        const inlineIds = markdownEditorRef.current.getInlineAttachmentIds()
        for (const attId of inlineIds) {
          try {
            await api.patch(`tickets/attachments/${attId}/`, { comment: commentId })
          } catch (e) { /* ignore */ }
        }
      }

      for (const file of pendingFiles) {
        const formData = new FormData()
        formData.append('file', file)
        formData.append('ticket', id)
        if (commentId) formData.append('comment', commentId)
        await api.post(`tickets/${id}/attachments/`, formData, {
          headers: { 'Content-Type': 'multipart/form-data' },
        })
      }
      setNewComment('')
      localStorage.removeItem(draftKey)
      setPendingFiles([])
      setIsInternal(false)
      setReplyOpen(false)
      fetchTicket()
      enqueueSnackbar('Enviado!', { variant: 'success' })
    } catch (err) {
      console.error(err)
      enqueueSnackbar('Erro ao enviar.', { variant: 'error' })
    } finally {
      setSendingComment(false)
    }
  }

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight={400}>
        <CircularProgress />
      </Box>
    )
  }

  if (!ticket) return null

  const statusStyle = STATUS_COLORS[ticket.status] || STATUS_COLORS.OPEN
  const priorityStyle = PRIORITY_COLORS[ticket.priority] || PRIORITY_COLORS.MEDIUM

  // Combinar descrição como primeira mensagem + comentários
  const allMessages = [
    {
      id: 'description',
      user: ticket.created_by,
      message: ticket.description,
      created_at: ticket.created_at,
      attachments: ticket.attachments?.filter(a => !a.comment) || [],
      is_opening: true,
    },
    ...(ticket.comments || []),
  ]

  const renderAttachments = (attachments) => {
    if (!attachments?.length) return null
    const images = attachments.filter(att => /\.(jpg|jpeg|png|gif|webp|bmp|svg|heic)$/i.test(att.filename || att.file))
    const audios = attachments.filter(att => isAudioFile(att.filename || att.file))
    const others = attachments.filter(att => !isAudioFile(att.filename || att.file) && !/\.(jpg|jpeg|png|gif|webp|bmp|svg|heic)$/i.test(att.filename || att.file))
    return (
      <Box mt={1.5}>
        <Divider sx={{ mb: 1.5 }} />
        {images.length > 0 && (
          <Box display="flex" gap={0.5} flexWrap="wrap" mb={0.5}>
            {images.map(att => (
              <Box key={att.id} onClick={() => setImagePreview(att)} sx={{ borderRadius: 1.5, overflow: 'hidden', border: '1px solid #E2E8F0', cursor: 'pointer', '&:hover': { opacity: 0.85 } }}>
                <Box component="img" src={att.file} sx={{ width: 120, height: 90, objectFit: 'cover', display: 'block' }} />
              </Box>
            ))}
          </Box>
        )}
        {audios.length > 0 && (
          <Box display="flex" flexDirection="column" gap={1} mb={others.length > 0 ? 1.5 : 0}>
            {audios.map(att => (
              <AudioPlayer key={att.id} src={att.file} attachmentId={att.id} ticketId={ticket.id} existingTranscription={att.transcription} />
            ))}
          </Box>
        )}
        {others.length > 0 && (
          <Box display="flex" gap={1} flexWrap="wrap">
            {others.map(att => {
              const info = getFileTypeInfo(att.filename || att.file)
              const FileIcon = info.icon
              return (
                <Tooltip key={att.id} title={att.filename || 'Arquivo'} arrow>
                  <Box
                    component="a"
                    href={att.file}
                    target="_blank"
                    sx={{
                      display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
                      width: 64, height: 64, borderRadius: 2, bgcolor: info.bg, border: '1px solid #E2E8F0',
                      textDecoration: 'none', cursor: 'pointer', transition: 'all 0.15s',
                      '&:hover': { transform: 'scale(1.05)', boxShadow: '0 2px 8px rgba(0,0,0,0.1)' },
                    }}
                  >
                    <FileIcon sx={{ fontSize: 24, color: info.color }} />
                    <Typography variant="caption" sx={{ fontSize: 9, fontWeight: 700, color: info.color, mt: 0.25 }}>{info.label}</Typography>
                  </Box>
                </Tooltip>
              )
            })}
          </Box>
        )}
      </Box>
    )
  }

  const renderReadReceipt = (msg) => {
    if (!msg.user?.is_superuser || msg.is_internal || msg.is_opening) return null
    const clientReceipt = ticket.read_receipts?.find(r => r.user?.id === ticket.created_by?.id)
    const readOnPlatform = clientReceipt && moment(clientReceipt.last_read_at).isAfter(msg.created_at)
    const readOnEmail = clientReceipt?.email_read_at && moment(clientReceipt.email_read_at).isAfter(msg.created_at)
    if (readOnPlatform) return <Tooltip title={`Lido na plataforma ${moment(clientReceipt.last_read_at).fromNow()}`} arrow><DoneAll sx={{ fontSize: 14, color: '#0073ea', cursor: 'default' }} /></Tooltip>
    if (readOnEmail) return <Tooltip title={`Visto no email ${moment(clientReceipt.email_read_at).fromNow()}`} arrow><DoneAll sx={{ fontSize: 14, color: '#94A3B8', cursor: 'default' }} /></Tooltip>
    return <Tooltip title="Não lido" arrow><Done sx={{ fontSize: 14, color: '#94A3B8', cursor: 'default' }} /></Tooltip>
  }

  return (
    <Box>
      {/* Header */}
      <Box display="flex" alignItems="center" gap={1} mb={2}>
        <Button startIcon={<ArrowBack />} onClick={() => navigate('/admin/tickets')}>Voltar</Button>
      </Box>

      {/* Title bar */}
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Box display="flex" alignItems="center" gap={1.5}>
          <Typography variant="body2" sx={{ fontFamily: 'monospace', fontWeight: 600, color: 'text.secondary' }}>{ticket.protocol}</Typography>
          <Typography variant="h6" sx={{ fontWeight: 700 }}>{ticket.title}</Typography>
        </Box>
        <Box display="flex" gap={1} alignItems="center">
          <Chip label={priorityStyle.label} size="small" sx={{ bgcolor: priorityStyle.bg, color: priorityStyle.color, fontWeight: 600 }} />
          <Chip label={statusStyle.label} size="small" sx={{ bgcolor: statusStyle.bg, color: statusStyle.color, fontWeight: 600 }} />
          {ticket.feedback && (() => {
            const cfg = RATING_CONFIG[ticket.feedback.rating] || RATING_CONFIG[3]
            const Icon = cfg.icon
            return (
              <Tooltip title={`Feedback: ${cfg.label} (${ticket.feedback.rating}/5)`} arrow>
                <Chip
                  icon={<Icon sx={{ fontSize: '18px !important', color: `${cfg.color} !important` }} />}
                  label={cfg.label}
                  size="small"
                  onClick={() => setFeedbackDetailOpen(true)}
                  sx={{
                    cursor: 'pointer', fontWeight: 600,
                    bgcolor: `${cfg.color}15`, color: cfg.color,
                    border: `1px solid ${cfg.color}30`,
                    '&:hover': { bgcolor: `${cfg.color}25` },
                  }}
                />
              </Tooltip>
            )
          })()}
        </Box>
      </Box>

      <Box display="flex" gap={3} flexDirection={{ xs: 'column', md: 'row' }}>
        <Box flex={1}>
          {/* Messages as individual cards */}
          <Box display="flex" flexDirection="column-reverse" gap={2}>
            {allMessages.map(msg => {
              const isStaff = msg.user?.is_superuser
              return (
                <Card key={msg.id} sx={{
                  border: msg.is_opening ? '1px solid #E2E8F0' : msg.is_internal ? '1px dashed #FDBA74' : 'none',
                  bgcolor: msg.is_internal ? '#FFF7ED' : '#fff',
                }}>
                  <CardContent sx={{ p: 2.5, '&:last-child': { pb: 2.5 } }}>
                    <Box display="flex" justifyContent="space-between" alignItems="center" mb={1.5}>
                      <Box display="flex" alignItems="center" gap={1}>
                        <Avatar src={msg.user?.photo || undefined} sx={{ width: 28, height: 28, bgcolor: isStaff ? '#1E3040' : '#94A3B8', fontSize: 12, ...(isStaff && { border: '2px solid #0073ea' }) }}>
                          {msg.user?.first_name?.[0] || 'U'}
                        </Avatar>
                        <Box component="span" sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5 }}>
                          <Typography component="span" variant="body2" sx={{ fontWeight: 600, color: '#1E293B', lineHeight: 1 }}>
                            {msg.user?.first_name} {msg.user?.last_name}
                          </Typography>
                          {isStaff && <Tooltip title="Equipe Bloco B3" arrow><Verified sx={{ fontSize: 16, color: '#0073ea', cursor: 'default', display: 'block' }} /></Tooltip>}
                        </Box>
                        {msg.is_opening && <Chip label="Abertura" size="small" sx={{ height: 20, fontSize: 10, bgcolor: '#DBEAFE', color: '#1E40AF' }} />}
                        {msg.is_internal && (
                          <>
                            <Chip label="Interno" size="small" icon={<Lock sx={{ fontSize: '10px !important' }} />} sx={{ height: 20, fontSize: 10, bgcolor: '#FED7AA', color: '#9A3412' }} />
                            <Tooltip title="Excluir nota interna" arrow>
                              <IconButton
                                size="small"
                                onClick={(e) => { e.stopPropagation(); handleDeleteComment(msg.id) }}
                                sx={{ ml: 0.5, p: 0.3, color: '#DC2626', opacity: 0.5, '&:hover': { opacity: 1, bgcolor: '#FEE2E2' } }}
                              >
                                <Delete sx={{ fontSize: 14 }} />
                              </IconButton>
                            </Tooltip>
                          </>
                        )}
                      </Box>
                      <Box component="span" sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.3 }}>
                        <Tooltip title={moment(msg.created_at).format('DD/MM/YYYY [às] HH:mm')} arrow>
                          <Typography variant="caption" color="text.secondary" sx={{ cursor: 'default' }}>{moment(msg.created_at).fromNow()}</Typography>
                        </Tooltip>
                        {renderReadReceipt(msg)}
                      </Box>
                    </Box>
                    <Box sx={{ color: '#334155', fontSize: '0.95rem', lineHeight: 1.7, '& p': { m: 0, mb: 1 }, '& p:last-child': { mb: 0 }, '& ul, & ol': { m: 0, pl: 2.5, mb: 1 }, '& code': { bgcolor: '#F1F5F9', px: 0.5, py: 0.25, borderRadius: 0.5, fontSize: '0.85em', fontFamily: 'monospace' }, '& pre': { bgcolor: '#F1F5F9', p: 1.5, borderRadius: 1, overflow: 'auto', mb: 1 }, '& pre code': { bgcolor: 'transparent', p: 0 }, '& a': { color: '#0073ea', textDecoration: 'none', '&:hover': { textDecoration: 'underline' } }, '& blockquote': { borderLeft: '3px solid #CBD5E1', pl: 1.5, ml: 0, color: '#64748B' }, '& strong': { fontWeight: 600 } }}>
                      <Markdown remarkPlugins={[remarkBreaks]} components={{
                        img: ({ src, alt }) => (
                          <Box
                            component="img"
                            src={src}
                            alt={alt || ''}
                            onClick={(e) => { e.stopPropagation(); setImagePreview({ file: src, filename: alt || 'imagem' }) }}
                            sx={{ maxWidth: 600, maxHeight: 400, borderRadius: 1.5, border: '1px solid #E2E8F0', cursor: 'pointer', display: 'block', my: 1, '&:hover': { opacity: 0.85 } }}
                          />
                        ),
                      }}>{msg.message}</Markdown>
                    </Box>
                    {renderAttachments(msg.attachments)}
                  </CardContent>
                </Card>
              )
            })}
          </Box>

          {/* Investigation Steps */}
          {ticket.investigations?.length > 0 && (
            <Box sx={{ mt: 3 }}>
              <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1.5, display: 'flex', alignItems: 'center', gap: 1 }}>
                Investigações da IA
              </Typography>
              <InvestigationSteps investigations={ticket.investigations} onDelete={() => fetchTicket()} />
            </Box>
          )}
        </Box>

        {/* Sidebar */}
        <Box sx={{ width: { xs: '100%', md: 280 }, flexShrink: 0 }}>
          <Card sx={{ mb: 2 }}>
            <CardContent sx={{ p: 2.5 }}>
              <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1.5 }}>Ações</Typography>
              <Box display="flex" flexDirection="column" gap={1}>
                <Button fullWidth variant="contained" startIcon={<Reply />} onClick={() => setReplyOpen(true)} sx={{ bgcolor: '#1E3040', textTransform: 'none', fontWeight: 600, '&:hover': { bgcolor: '#0F1C2E' } }}>
                  Responder ticket
                </Button>
                <Button
                  fullWidth
                  variant={ticket.is_pinned ? "contained" : "outlined"}
                  startIcon={<PushPin />}
                  onClick={handleTogglePin}
                  sx={ticket.is_pinned ? {
                    bgcolor: '#F59E0B', color: '#78350F', textTransform: 'none', fontWeight: 600,
                    '&:hover': { bgcolor: '#D97706' },
                  } : {
                    color: '#64748B', borderColor: '#CBD5E1', textTransform: 'none', fontWeight: 600,
                    '&:hover': { borderColor: '#94A3B8', bgcolor: '#F8FAFC' },
                  }}
                >
                  {ticket.is_pinned ? 'Em acompanhamento' : 'Manter em acompanhamento'}
                </Button>
                <Divider sx={{ my: 0.5 }} />
                <Button
                  fullWidth
                  variant="outlined"
                  startIcon={<SmartToy />}
                  onClick={() => setAiInvestigateOpen(true)}
                  sx={{ color: '#7C3AED', borderColor: '#DDD6FE', textTransform: 'none', fontWeight: 600, '&:hover': { borderColor: '#7C3AED', bgcolor: '#F5F3FF' } }}
                >
                  Investigar com IA
                </Button>
                <Divider sx={{ my: 0.5 }} />
                <FormControl fullWidth size="small">
                  <InputLabel>Status</InputLabel>
                  <Select value={ticket.status} label="Status" onChange={(e) => handleStatusChange(e.target.value)} disabled={statusLoading}>
                    <MenuItem value="OPEN">Aberto</MenuItem>
                    <MenuItem value="IN_PROGRESS">Em Andamento</MenuItem>
                    <MenuItem value="WAITING_CLIENT">Aguardando Cliente</MenuItem>
                    <MenuItem value="RESOLVED">Finalizado</MenuItem>
                  </Select>
                </FormControl>
                <Divider sx={{ my: 0.5 }} />
                <Button
                  fullWidth
                  variant="outlined"
                  startIcon={<OpenInNew />}
                  onClick={async () => {
                    try {
                      const condoId = ticket.condo?.id
                      if (!condoId) return
                      const res = await api.get(`/condo/${condoId}`)
                      localStorage.setItem('condoId', res.data.id)
                      localStorage.setItem('condoName', res.data.name)
                      localStorage.setItem('condoType', res.data.type)
                      localStorage.setItem('rentType', res.data.rent_type || '')
                      localStorage.setItem('cf', res.data.enable_cash_flow || '')
                      try {
                        const featRes = await api.get(`/plan/condo-features/${condoId}/`)
                        localStorage.setItem('condoFeatures', JSON.stringify(featRes.data.features || []))
                      } catch { localStorage.setItem('condoFeatures', '[]') }
                      window.open('/', '_blank')
                    } catch (err) {
                      enqueueSnackbar('Erro ao acessar empresa.', { variant: 'error' })
                    }
                  }}
                  sx={{ textTransform: 'none', fontWeight: 600, color: '#475569', borderColor: '#CBD5E1', '&:hover': { borderColor: '#94A3B8', bgcolor: '#F8FAFC' } }}
                >
                  Entrar na empresa
                </Button>
              </Box>
            </CardContent>
          </Card>

          <Card>
            <CardContent sx={{ p: 2.5 }}>
              <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 2 }}>Detalhes</Typography>
              <Box display="flex" flexDirection="column" gap={2}>
                <Box>
                  <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 600, textTransform: 'uppercase', fontSize: 10 }}>Categoria</Typography>
                  <Box display="flex" alignItems="center" gap={0.5} mt={0.25}><Category sx={{ fontSize: 16, color: 'text.secondary' }} /><Typography variant="body2">{ticket.category?.name}</Typography></Box>
                </Box>
                <Box>
                  <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 600, textTransform: 'uppercase', fontSize: 10 }}>Atribuído a</Typography>
                  {ticket.assigned_to ? (
                    <Box display="flex" alignItems="center" gap={0.5} mt={0.25}>
                      <Person sx={{ fontSize: 16, color: 'text.secondary' }} />
                      <Typography variant="body2">{ticket.assigned_to?.first_name} {ticket.assigned_to?.last_name}</Typography>
                    </Box>
                  ) : (
                    <Box mt={0.5}>
                      <Button
                        size="small"
                        startIcon={<PersonAdd sx={{ fontSize: 14 }} />}
                        onClick={() => {
                          api.post(`tickets/${id}/assign/`, { assigned_to: user?.id })
                            .then(res => { setTicket(res.data); enqueueSnackbar('Ticket atribuído a você!', { variant: 'success' }) })
                            .catch(() => enqueueSnackbar('Erro ao atribuir.', { variant: 'error' }))
                        }}
                        sx={{ textTransform: 'none', fontSize: 12, color: '#0073ea' }}
                      >
                        Atribuir a mim
                      </Button>
                    </Box>
                  )}
                </Box>

                <Box>
                  <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 600, textTransform: 'uppercase', fontSize: 10 }}>Criado por</Typography>
                  <Box display="flex" alignItems="center" gap={0.5} mt={0.25}><Person sx={{ fontSize: 16, color: 'text.secondary' }} /><Typography variant="body2">{ticket.created_by?.first_name} {ticket.created_by?.last_name}</Typography></Box>
                  <Typography variant="caption" color="text.secondary">{ticket.created_by?.email}</Typography>
                </Box>
                <Box>
                  <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 600, textTransform: 'uppercase', fontSize: 10 }}>Empresa</Typography>
                  <Typography variant="body2" sx={{ mt: 0.25 }}>{ticket.condo?.name}</Typography>
                </Box>
                <Box>
                  <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 600, textTransform: 'uppercase', fontSize: 10 }}>Criado em</Typography>
                  <Box display="flex" alignItems="center" gap={0.5} mt={0.25}><AccessTime sx={{ fontSize: 16, color: 'text.secondary' }} /><Typography variant="body2">{moment(ticket.created_at).format('DD/MM/YYYY HH:mm')}</Typography></Box>
                </Box>

                <Box>
                  <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 600, textTransform: 'uppercase', fontSize: 10 }}>
                    Tempo do suporte
                  </Typography>
                  <Tooltip title="Conta apenas o tempo em que o suporte estava com a bola (excluindo tempo de espera do cliente)" arrow>
                    <Box display="flex" alignItems="center" gap={0.5} mt={0.25}>
                      <AccessTime sx={{ fontSize: 16, color: ticket.sla_status === 'overdue' ? '#DC2626' : ticket.sla_status === 'warning' ? '#D97706' : 'text.secondary' }} />
                      <Typography variant="body2" sx={{ fontWeight: 600, color: ticket.sla_status === 'overdue' ? '#DC2626' : ticket.sla_status === 'warning' ? '#D97706' : 'inherit' }}>
                        {ticket.support_time || ticket.elapsed_time}
                      </Typography>
                    </Box>
                  </Tooltip>
                </Box>

                {ticket.due_date && (
                  <Box>
                    <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 600, textTransform: 'uppercase', fontSize: 10 }}>Prazo (SLA)</Typography>
                    <Box display="flex" alignItems="center" gap={0.5} mt={0.25}><AccessTime sx={{ fontSize: 16, color: moment(ticket.due_date).isBefore(moment()) ? '#DC2626' : 'text.secondary' }} /><Typography variant="body2" sx={{ color: moment(ticket.due_date).isBefore(moment()) ? '#DC2626' : 'inherit', fontWeight: moment(ticket.due_date).isBefore(moment()) ? 600 : 400 }}>{moment(ticket.due_date).format('DD/MM/YYYY HH:mm')}</Typography></Box>
                  </Box>
                )}
                {ticket.closed_at && (
                  <Box>
                    <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 600, textTransform: 'uppercase', fontSize: 10 }}>Finalizado em</Typography>
                    <Typography variant="body2" sx={{ mt: 0.25 }}>{moment(ticket.closed_at).format('DD/MM/YYYY HH:mm')}</Typography>
                  </Box>
                )}
              </Box>
            </CardContent>
          </Card>

        </Box>
      </Box>

      {/* Reply modal */}
      <Dialog open={replyOpen} onClose={() => setReplyOpen(false)} fullWidth maxWidth="md">
        <DialogContent sx={{ p: 3 }}>
          <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
            <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>Responder</Typography>
            <IconButton size="small" onClick={() => setReplyOpen(false)}><Close /></IconButton>
          </Box>

          {pendingFiles.length > 0 && (
            <Box display="flex" gap={1} flexWrap="wrap" mb={1.5}>
              {pendingFiles.map((file, index) => (
                <Box key={index} sx={{ display: 'flex', alignItems: 'center', gap: 1, p: 1, borderRadius: 1.5, bgcolor: '#F8FAFC', border: '1px solid #E2E8F0' }}>
                  {isAudioMimeType(file.type) ? (
                    <AudioPlayer src={URL.createObjectURL(file)} />
                  ) : file.type?.startsWith('image/') ? (
                    <Box component="img" src={URL.createObjectURL(file)} sx={{ width: 36, height: 36, borderRadius: 1, objectFit: 'cover' }} />
                  ) : (
                    <InsertDriveFile sx={{ fontSize: 24, color: '#94A3B8' }} />
                  )}
                  {!isAudioMimeType(file.type) && (
                    <Box minWidth={0}>
                      <Typography variant="caption" sx={{ fontWeight: 500, color: '#334155', display: 'block', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: 120 }}>{file.name}</Typography>
                      <Typography variant="caption" sx={{ color: '#94A3B8', fontSize: 10 }}>{(file.size / 1024).toFixed(0)} KB</Typography>
                    </Box>
                  )}
                  <IconButton size="small" onClick={() => handleRemovePendingFile(index)} sx={{ color: '#94A3B8', p: 0.25 }}><Close sx={{ fontSize: 14 }} /></IconButton>
                </Box>
              ))}
            </Box>
          )}

          <MarkdownEditor
            ref={markdownEditorRef}
            value={newComment}
            onChange={(val) => { setNewComment(val); localStorage.setItem(draftKey, val) }}
            placeholder={isInternal ? 'Nota interna (não visível ao cliente)...' : 'Digite sua resposta...'}
            disabled={sendingComment}
            ticketId={id}
          />

          <input type="file" ref={fileInputRef} style={{ display: 'none' }} onChange={handleFileSelect} multiple accept="image/*,audio/*,.pdf,.doc,.docx,.xls,.xlsx,.txt,.csv,.zip,.rar" />
          <Divider sx={{ my: 2 }} />
          <Box display="flex" justifyContent="flex-end" alignItems="center" gap={0.5}>
            {isRecording ? (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <FiberManualRecord sx={{ fontSize: 14, color: '#EF4444', animation: 'pulse 1.2s infinite', '@keyframes pulse': { '0%, 100%': { opacity: 1 }, '50%': { opacity: 0.3 } } }} />
                <Typography variant="caption" sx={{ fontWeight: 600, color: '#EF4444', fontFamily: 'monospace' }}>{formattedTime}</Typography>
                <Tooltip title="Parar e salvar"><IconButton size="small" onClick={stopRecording} sx={{ color: '#0073ea' }}><Stop /></IconButton></Tooltip>
                <Tooltip title="Cancelar"><IconButton size="small" onClick={cancelRecording} sx={{ color: '#94A3B8' }}><Close /></IconButton></Tooltip>
              </Box>
            ) : (
              <>
                <Tooltip title="Anexar arquivos"><IconButton onClick={() => fileInputRef.current?.click()} disabled={sendingComment} sx={{ color: '#94A3B8' }}><AttachFile /></IconButton></Tooltip>
                <Tooltip title="Gravar áudio"><IconButton onClick={startRecording} disabled={sendingComment} sx={{ color: '#94A3B8' }}><Mic /></IconButton></Tooltip>
              </>
            )}
            <Tooltip title={isInternal ? 'Nota interna (não visível ao cliente)' : 'Resposta pública'}>
              <IconButton onClick={() => setIsInternal(!isInternal)} sx={{ color: isInternal ? '#D97706' : '#94A3B8' }}><Lock /></IconButton>
            </Tooltip>
            {isInternal && <Typography variant="caption" sx={{ color: '#D97706', mr: 0.5 }}>Interno</Typography>}
            <Button
              variant="contained"
              startIcon={sendingComment ? <CircularProgress size={16} color="inherit" /> : <Send />}
              onClick={handleSendComment}
              disabled={sendingComment || isRecording || (!newComment.trim() && pendingFiles.length === 0)}
              sx={{ bgcolor: '#1E3040', textTransform: 'none', fontWeight: 600, '&:hover': { bgcolor: '#0F1C2E' }, ml: 1 }}
            >
              Enviar
            </Button>
          </Box>
        </DialogContent>
      </Dialog>

      {/* Image preview modal */}
      <Dialog open={!!imagePreview} onClose={() => setImagePreview(null)} maxWidth={false} PaperProps={{ sx: { bgcolor: '#fff', maxWidth: '92vw', borderRadius: 2 } }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', px: 2, py: 1.5, borderBottom: '1px solid #E2E8F0' }}>
          <Typography variant="body2" sx={{ fontWeight: 600, color: '#334155', maxWidth: 400, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
            {imagePreview?.filename || 'Imagem'}
          </Typography>
          <IconButton size="small" onClick={() => setImagePreview(null)}><Close sx={{ fontSize: 20 }} /></IconButton>
        </Box>
        <DialogContent sx={{ p: 2, display: 'flex', justifyContent: 'center', alignItems: 'center', bgcolor: '#F8FAFC' }}>
          <Box component="img" src={imagePreview?.file} sx={{ maxWidth: '88vw', maxHeight: '78vh', borderRadius: 1, objectFit: 'contain' }} />
        </DialogContent>
      </Dialog>

      {/* Feedback detail modal */}
      {ticket?.feedback && (
        <Dialog open={feedbackDetailOpen} onClose={() => setFeedbackDetailOpen(false)} maxWidth="sm" fullWidth>
          <DialogContent sx={{ p: 3 }}>
            <Box display="flex" justifyContent="space-between" alignItems="center" mb={2.5}>
              <Typography variant="h6" sx={{ fontWeight: 700 }}>Feedback do Cliente</Typography>
              <IconButton size="small" onClick={() => setFeedbackDetailOpen(false)}><Close /></IconButton>
            </Box>

            <Box display="flex" alignItems="center" gap={2} mb={3}>
              {(() => {
                const cfg = RATING_CONFIG[ticket.feedback.rating] || RATING_CONFIG[3]
                const Icon = cfg.icon
                return (
                  <Box sx={{
                    width: 56, height: 56, borderRadius: 2, display: 'flex', alignItems: 'center', justifyContent: 'center',
                    bgcolor: `${cfg.color}15`,
                  }}>
                    <Icon sx={{ fontSize: 36, color: cfg.color }} />
                  </Box>
                )
              })()}
              <Box>
                <Typography variant="h6" sx={{ fontWeight: 700 }}>{ticket.feedback.rating_display}</Typography>
                <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                  {moment(ticket.feedback.created_at).format('DD/MM/YYYY [às] HH:mm')}
                </Typography>
              </Box>
            </Box>

            {ticket.feedback.reason_display && (
              <Box mb={2}>
                <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 600, textTransform: 'uppercase', fontSize: 10 }}>Motivo</Typography>
                <Typography variant="body1" sx={{ mt: 0.5 }}>{ticket.feedback.reason_display}</Typography>
              </Box>
            )}

            {ticket.feedback.comment && (
              <Box>
                <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 600, textTransform: 'uppercase', fontSize: 10 }}>Comentário do cliente</Typography>
                <Box sx={{ mt: 0.5, p: 2, bgcolor: '#F8FAFC', borderRadius: 2, border: '1px solid #E2E8F0' }}>
                  <Typography variant="body2" sx={{ fontStyle: 'italic', color: '#475569', whiteSpace: 'pre-wrap' }}>
                    "{ticket.feedback.comment}"
                  </Typography>
                </Box>
              </Box>
            )}
          </DialogContent>
        </Dialog>
      )}

      <TicketAIInvestigator
        open={aiInvestigateOpen}
        onClose={() => setAiInvestigateOpen(false)}
        ticketId={id}
      />
    </Box>
  )
}

export default AdminTicketDetail
